import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAccounts } from '../api/admin.ts';
import { ROLE_OPS_ADMIN } from '../auth/opsRoles.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { CommandPalette } from '../ui/index.ts';
import type { CommandGroup } from '../ui/index.ts';
import { canAccessRoles, visibleNavGroups } from './navigation.ts';
import { loadRecentPaths } from './recentPaths.ts';

/**
 * ⌘K palette wiring for the Shell (epic #835 PR2). Sources, all real:
 *
 *   1. Navigation — the operator's role-filtered nav items.
 *   2. Recent — the last visited nav destinations (localStorage).
 *   3. Accounts — live email search against `/v1/ops/admin/accounts`
 *      once the query looks like an email fragment (admin only).
 *   4. Request deep-link — a `req_…` query jumps straight into the
 *      request logs with the id prefilled (the page reads
 *      `?requestId=` from the URL).
 *
 * Filtering is manual (`shouldFilter` off in the primitive) so async
 * account hits and the request deep-link coexist with static items.
 */
interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

const ACCOUNT_SEARCH_MIN_CHARS = 3;

export function ShellCommandPalette({ open, onOpenChange }: Props): JSX.Element {
  const { user, fetchImpl } = useSession();
  const navigate = useNavigate();
  const t = useT();
  const [query, setQuery] = useState('');
  const rawRoles = useMemo(() => user?.roles ?? [], [user]);
  const isAdmin = canAccessRoles(rawRoles, [ROLE_OPS_ADMIN]);

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const normalized = query.trim().toLowerCase();

  const accountSearchEnabled =
    open && isAdmin && normalized.includes('@') && normalized.length >= ACCOUNT_SEARCH_MIN_CHARS;
  const accountsQuery = useQuery({
    queryKey: ['palette-accounts', normalized],
    queryFn: ({ signal }) => listAccounts({ email: normalized, limit: 5 }, fetchImpl, signal),
    enabled: accountSearchEnabled,
    staleTime: 30_000,
  });

  function go(path: string): void {
    onOpenChange(false);
    navigate(path);
  }

  const groups = useMemo<readonly CommandGroup[]>(() => {
    const out: CommandGroup[] = [];

    // Request deep-link — an exact-looking request id beats everything.
    if (isAdmin && /^req_[a-z0-9]/i.test(query.trim())) {
      const requestId = query.trim();
      out.push({
        heading: t('shell.cmdk.group.requests'),
        items: [
          {
            id: `req:${requestId}`,
            label: t('shell.cmdk.open_request').replace('{id}', requestId),
            hint: 'req',
            onSelect: () => go(`/customers/requests?requestId=${encodeURIComponent(requestId)}`),
          },
        ],
      });
    }

    // Live account hits.
    const accounts = accountSearchEnabled ? (accountsQuery.data?.accounts ?? []) : [];
    if (accounts.length > 0) {
      out.push({
        heading: t('shell.cmdk.group.accounts'),
        items: accounts.map((account) => ({
          id: `acc:${account.account_id}`,
          label: account.email,
          hint: account.account_id,
          onSelect: () => go(`/customers/accounts/${encodeURIComponent(account.account_id)}`),
        })),
      });
    }

    // Recent destinations (only when not searching).
    const navGroups = visibleNavGroups(rawRoles);
    const allItems = navGroups.flatMap((group) => group.items);
    if (normalized.length === 0) {
      const recent = loadRecentPaths()
        .map((path) => allItems.find((item) => item.to === path))
        .filter((item): item is NonNullable<typeof item> => item !== undefined)
        .slice(0, 5);
      if (recent.length > 0) {
        out.push({
          heading: t('shell.cmdk.group.recent'),
          items: recent.map((item) => ({
            id: `recent:${item.to}`,
            label: t(item.labelKey),
            hint: item.to,
            onSelect: () => go(item.to),
          })),
        });
      }
    }

    // Navigation — manually filtered against label, path, and group.
    for (const group of navGroups) {
      const items = group.items.filter((item) => {
        if (normalized.length === 0) {
          return true;
        }
        const haystack = `${t(item.labelKey)} ${item.to} ${t(group.titleKey)}`.toLowerCase();
        return haystack.includes(normalized);
      });
      if (items.length > 0) {
        out.push({
          heading: t(group.titleKey),
          items: items.map((item) => ({
            id: `nav:${item.to}`,
            label: t(item.labelKey),
            hint: item.to,
            onSelect: () => go(item.to),
          })),
        });
      }
    }

    return out;
  }, [
    query,
    normalized,
    isAdmin,
    accountSearchEnabled,
    accountsQuery.data,
    rawRoles,
    t,
    navigate,
    onOpenChange,
  ]);

  return (
    <CommandPalette
      open={open}
      onOpenChange={onOpenChange}
      groups={groups}
      search={query}
      onSearchChange={setQuery}
      placeholder={t('shell.cmdk.placeholder')}
      emptyText={t('shell.cmdk.empty')}
      ariaLabel={t('shell.cmdk.aria_label')}
      footer={t('shell.cmdk.footer_hint')}
      testId="shell-cmdk"
    />
  );
}
